import { call, put, take, takeLatest } from "redux-saga/effects"
import { apiFunctions } from "../../api/functions"
import { errorHandler } from "../../utils/errorHandling"
import {
    startSessionRequest,
    startSessionSuccess,
    startSessionFailure,
    submitVoteRequest,
    submitVoteSuccess,
    submitVoteFailure,
    fetchResultsRequest,
    fetchResultsSuccess,
    fetchResultsFailure,
    stopSessionRequest,
    stopSessionSuccess,
    stopSessionFailure,
    saveVotingDataRequest,
    saveVotingDataSuccess,
    saveVotingDataFailure,
    getVotingDataRequest,
    getVotingDataSuccess,
    getVotingDataFailure,
    clearVotingDataRequest,
    clearVotingDataSuccess,
    clearVotingDataFailure,
    getProtectiveMeasuresRequest,
    getProtectiveMeasuresSuccess,
    getProtectiveMeasuresFailure,
} from "./anonymousScoringSlice"

const startSessionAPI = apiFunctions.startSession
const submitVoteAPI = apiFunctions.submitVote
const fetchResultsAPI = apiFunctions.fetchResults
const stopSessionAPI = apiFunctions.stopSession
const saveVotingDataAPI = apiFunctions.saveVotingData
const getVotingDataAPI = apiFunctions.getVotingData
const clearVotingDataAPI = apiFunctions.clearVotingData
const getProtectiveMeasuresAPI = apiFunctions.getProtectiveMeasures

async function startSessionHandlerAPI() {
    const response = await startSessionAPI();
    if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    if (response.data.session_id) {
        return response.data.session_id;
    }
    if (response.headers.get('content-type')?.includes('application/json')) {
        return response.json();
    } else {
        throw new Error('Unexpected response format');
    }
}

function* startSessionHandler() {
    try{
        const result = yield call(startSessionHandlerAPI)
        yield put(startSessionSuccess({sessionId: result}))
    }
    catch (error){
        console.error("Error in startSessionHandler:", error);
        const err = errorHandler(error)
        yield put(startSessionFailure(err))
    }
}

async function submitVoteHandlerAPI(payload) {
    if (payload.sessionId === undefined || payload.sessionId === null) {
        throw new Error('Session ID is required');
    }
    const response = await submitVoteAPI(payload)
    if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    if (response.status === 200) {
        return response.status;
    }
    else {
        throw new Error('Unexpected response format');
    }
}

function* submitVoteHandler({payload}) {
    try{
        yield call(submitVoteHandlerAPI, payload)
        yield put(submitVoteSuccess())
    }
    catch (error){
        const err = errorHandler(error)
        yield put(submitVoteFailure(err))
    }
}

async function fetchResultsHandlerAPI(sessionId) {
    const response = await fetchResultsAPI(sessionId)
    if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    if (response.data.likelihood_results && response.data.severity_results && response.data.active) {
        return {
            'votes': {
                'likelihood_results': response.data.likelihood_results, 
                'severity_results': response.data.severity_results
            },
            'isSessionActive': response.data.active
        };
    }
    else {
        throw new Error('Unexpected response format');
    }
}

function* fetchResultsHandler({payload}) {
    try{
        const result = yield call(fetchResultsHandlerAPI, payload)
        yield put(fetchResultsSuccess(result))
    }
    catch (error){
        const err = errorHandler(error)
        yield put(fetchResultsFailure(err))
    }
}

async function stopSessionHandlerAPI(sessionId) {
    const response = await stopSessionAPI(sessionId);
    if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    if (response.status === 200) {
        return response.status;
    }
    else {
        throw new Error('Unexpected response format');
    }
}

function* stopSessionHandler({payload}) {
    try{
        yield call(stopSessionHandlerAPI, payload)
        yield put(stopSessionSuccess())
    }
    catch (error){
        const err = errorHandler(error)
        yield put(stopSessionFailure(err))
    }
}

async function saveVotingDataHandlerAPI(payload) {
    const response = await saveVotingDataAPI(payload);
    if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    if (response.status === 200) {
        return response.status;
    }
    else {
        throw new Error('Unexpected response format');
    }
}

function* saveVotingDataHandler({payload}) {
    try{
        yield call(saveVotingDataHandlerAPI, payload)
        yield put(saveVotingDataSuccess())
    }
    catch (error){
        const err = errorHandler(error)
        yield put(saveVotingDataFailure(err))
    }
}

async function getVotingDataHandlerAPI(eventTaskId) {
    const response = await getVotingDataAPI(eventTaskId);
    if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    if (response.status === 200) {
        return {
            'storedVotes': {
                'likelihood_results': response.data.likelihood_results, 
                'severity_results': response.data.severity_results,
                'likelihood_scoring_note': response.data.likelihood_scoring_note,
                'severity_scoring_note': response.data.severity_scoring_note
            }
        };
    }
    else {
        throw new Error('Unexpected response format');
    }
}

function* getVotingDataHandler({payload}) {
    try{
        const result = yield call(getVotingDataHandlerAPI, payload)
        yield put(getVotingDataSuccess(result))
    }
    catch (error){
        const err = errorHandler(error)
        yield put(getVotingDataFailure(err))
    }
}

async function clearVotingDataHandlerAPI(eventTaskId) {
    const response = await clearVotingDataAPI(eventTaskId);
    if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    if (response.status === 200) {
        return response.status;
    }
    else {
        throw new Error('Unexpected response format');
    }
}

function* clearVotingDataHandler({payload}) {
    try{
        const result = yield call(clearVotingDataHandlerAPI, payload)
        yield put(clearVotingDataSuccess(result))
    }
    catch (error){
        const err = errorHandler(error)
        yield put(clearVotingDataFailure(err))
    }
}

async function getProtectiveMeasuresHandlerAPI(eventTaskId) {
    const response = await getProtectiveMeasuresAPI(eventTaskId);
    if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    if (response.status === 200) {
        return {
            'protectiveMeasures': {
                'inherently_measures': response.data.inherently_measures, 
                'safeguarding': response.data.safeguarding,
                'info_for_use': response.data.info_for_use
            }
        };
    }
    else {
        throw new Error('Unexpected response format');
    }
}

function* getProtectiveMeasuresHandler({payload}) {
    try{
        const result = yield call(getProtectiveMeasuresHandlerAPI, payload)
        yield put(getProtectiveMeasuresSuccess(result))
    }
    catch (error){
        const err = errorHandler(error)
        yield put(getProtectiveMeasuresFailure(err))
    }
}

export function* anonymousScoringWatchers() {
  yield takeLatest(startSessionRequest.type, startSessionHandler)
  yield takeLatest(submitVoteRequest.type, submitVoteHandler)
  yield takeLatest(fetchResultsRequest.type, fetchResultsHandler)
  yield takeLatest(stopSessionRequest.type, stopSessionHandler)
  yield takeLatest(saveVotingDataRequest.type, saveVotingDataHandler)
  yield takeLatest(getVotingDataRequest.type, getVotingDataHandler)
  yield takeLatest(clearVotingDataRequest.type, clearVotingDataHandler)
  yield takeLatest(getProtectiveMeasuresRequest.type, getProtectiveMeasuresHandler)
}
